import { useCallback, useEffect, MouseEvent } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { GlobalState } from 'ha/types/store';
import { getIsAuthenticated } from 'ha/modules/AuthLogic/selectors';

import { useIntl } from 'ha/i18n';
import {
  addFavoriteListing,
  removeFavoriteListing,
  syncPendingFavoriteListing,
} from '../actions';

import {
  isLoading as isLoadingSelector,
  isFavorite as isFavoriteSelector,
} from '../selectors';

export const useFavoriteListing = (
  listingId: number,
  isTenantMULFlowEnabled: boolean,
) => {
  const isLoadingSelectorWithId = useCallback(
    (state: GlobalState) => isLoadingSelector(state, listingId),
    [listingId],
  );
  const isFavoriteSelectorWithId = useCallback(
    (state: GlobalState) => isFavoriteSelector(state, listingId),
    [listingId],
  );

  const isLoading = useSelector(isLoadingSelectorWithId);
  const isFavorite = useSelector(isFavoriteSelectorWithId);
  const isAuthenticated = useSelector(getIsAuthenticated);

  const location = useLocation();
  const dispatch = useDispatch();
  const { urlResolver } = useIntl();

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    dispatch(syncPendingFavoriteListing(isTenantMULFlowEnabled));
  }, [isAuthenticated]);

  const onClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();

      if (isLoading) {
        return;
      }

      if (isFavorite) {
        dispatch(removeFavoriteListing(listingId, isTenantMULFlowEnabled));
      } else {
        dispatch(
          addFavoriteListing(
            listingId,
            isTenantMULFlowEnabled,
            `${location.pathname}${location.search}`,
            urlResolver,
          ),
        );
      }
    },
    [
      dispatch,
      listingId,
      isLoading,
      isFavorite,
      location,
      isTenantMULFlowEnabled,
      urlResolver,
    ],
  );

  return { onClick, isLoading, isFavorite };
};
